import { useState, useEffect } from 'react'
import Wordmark from '@/components/layout/Wordmark'
import { API_BASE_URL } from '@/lib/api'
import { useBrand } from '@/hooks/useBrand'
import { Search, PhoneCall, MessageSquare, Globe, Sparkles, AlertTriangle } from 'lucide-react'

type DemoStatus =
  | 'form'
  | 'analyzing'
  | 'ready_to_call'
  | 'rejected_not_law_firm'
  | 'calling'
  | 'in_progress'
  | 'recapping'
  | 'completed'
  | 'failed'

interface IntakeRecap {
  intake_completeness_score: number
  headline: string
  facts_captured: string[]
  topics_covered: string[]
  would_have_booked: boolean
  follow_up_actions: string[]
}

/**
 * Calling-state card — the "your phone is about to ring" surface.
 * Hoisted to module scope so it doesn't remount on every parent render
 * (would reset the pulse-ring animation phase + the late-arrival timer).
 */
interface CallingStateCardProps {
  firmName: string | null
  caseType: string | null
  demoId: string | null
  onForceRefresh: () => void | Promise<void>
  EASE_OUT_STRONG: string
}

function CallingStateCard({ firmName, caseType, onForceRefresh, EASE_OUT_STRONG }: CallingStateCardProps) {
  // After 60s in this state, surface a manual "I already hung up" link.
  // Belt-and-braces in case the SSE drops AND polling is still in its
  // initial 60s grace window when the user hangs up early.
  const [showLateAffordance, setShowLateAffordance] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setShowLateAffordance(true), 60_000)
    return () => clearTimeout(t)
  }, [])

  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-6 py-10 sm:px-10 sm:py-12 opacity-0 animate-fade-in-up"
      style={{
        animationFillMode: 'forwards',
        animationTimingFunction: EASE_OUT_STRONG,
        // Real glass: inner top-edge highlight + tinted drop shadow that
        // reads as depth, not a neon glow.
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 28px 70px -25px rgba(13,10,31,0.7)',
      }}
    >
      {/* Pulse orb — three concentric expanding rings + a breathing core.
          All on transform/opacity only so they're hardware-accelerated and
          never block the main thread. The brand inner gradient sells the
          "AI on the line" feeling without using the cliché bouncing icon. */}
      <div className="flex justify-center mb-9">
        <div className="relative h-32 w-32">
          {[0, 0.6, 1.2].map((delay) => (
            <div
              key={delay}
              aria-hidden
              className="absolute inset-0 rounded-full border border-brand-royal/45"
              style={{
                animation: `pulseRingExpand 1.8s cubic-bezier(0.23, 1, 0.32, 1) ${delay}s infinite`,
              }}
            />
          ))}
          <div
            className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-indigo/30 via-brand-royal/35 to-brand-periwinkle/25 flex items-center justify-center"
            style={{
              animation: 'pulseCoreBreath 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -20px 40px rgba(77,113,253,0.28)',
            }}
          >
            <svg className="w-10 h-10 text-brand-periwinkle" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Caller ID — tabular-nums for the digit alignment, divider rule
          (border-t) instead of a nested card, generous letter-spacing
          on the number for the "incoming call screen" read. */}
      <div className="text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-semibold mb-2">
          Look for a call from
        </p>
        <p className="text-white font-semibold text-3xl sm:text-[2rem] tracking-[0.04em] tabular-nums">
          (661) 453-2349
        </p>
        <p className="text-white/50 text-sm mt-3">
          Answer when it rings — that's your AI receptionist on the line.
        </p>
      </div>

      {/* Personalization — sits under a hairline divider rather than
          inside a third nested card. Reads as supporting context. */}
      {firmName && (
        <div className="mt-8 pt-6 border-t border-white/[0.06]">
          <p className="text-center text-[13px] text-white/55">
            Personalized for{' '}
            <span className="text-white/85 font-medium">{firmName}</span>
            {caseType && (
              <>
                <span className="text-white/25 mx-2">·</span>
                <span className="text-white/70">{caseType}</span>
              </>
            )}
          </p>
        </div>
      )}

      {/* Late-arrival escape hatch — Emil: every flow needs an
          interruptible exit. After 60s without a status update, let the
          user nudge the system. This calls /status once and the parent
          updates state if the demo has completed in the meantime. */}
      {showLateAffordance && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onForceRefresh}
            className="text-[13px] text-white/55 hover:text-white underline-offset-4 hover:underline transition-colors duration-200 active:scale-[0.97]"
            style={{ transitionTimingFunction: EASE_OUT_STRONG }}
          >
            Already hung up? Check for the summary →
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Beat 1 card — site analysis in flight. Same glass-card chrome as the
 * calling card, but with a Globe icon and a 3-line process readout that
 * fades in as a stagger so it reads as work happening, not a spinner.
 */
interface AnalyzingCardProps {
  websiteUrl: string
  EASE_OUT_STRONG: string
}

function AnalyzingCard({ websiteUrl, EASE_OUT_STRONG }: AnalyzingCardProps) {
  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-6 py-10 sm:px-10 sm:py-12 opacity-0 animate-fade-in-up"
      style={{
        animationFillMode: 'forwards',
        animationTimingFunction: EASE_OUT_STRONG,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 28px 70px -25px rgba(13,10,31,0.7)',
      }}
    >
      <div className="flex justify-center mb-8">
        <div className="relative h-24 w-24">
          {[0, 0.6, 1.2].map((delay) => (
            <div
              key={delay}
              aria-hidden
              className="absolute inset-0 rounded-full border border-brand-royal/35"
              style={{
                animation: `pulseRingExpand 2s cubic-bezier(0.23, 1, 0.32, 1) ${delay}s infinite`,
              }}
            />
          ))}
          <div
            className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-indigo/30 via-brand-royal/35 to-brand-periwinkle/25 flex items-center justify-center"
            style={{
              animation: 'pulseCoreBreath 2.6s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -20px 40px rgba(77,113,253,0.28)',
            }}
          >
            <Globe className="w-8 h-8 text-brand-periwinkle" strokeWidth={1.6} />
          </div>
        </div>
      </div>

      <div className="text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/45 font-semibold mb-2">
          Reading
        </p>
        <p className="text-white font-medium text-lg sm:text-xl tracking-tight break-all px-2">
          {websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
        </p>
      </div>

      {/* 3-step process readout — staggered reveal, so each line fades in
          on its own beat. Hairline rail on the left for continuity with
          the form's 3-step list visual treatment. */}
      <ol className="relative mt-9 mx-auto inline-block text-left max-w-[18rem]">
        <div
          aria-hidden
          className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-brand-royal/40 via-white/10 to-transparent"
        />
        {[
          { label: 'Pulling pages', delay: '0ms' },
          { label: 'Checking practice areas', delay: '700ms' },
          { label: 'Confirming firm details', delay: '1400ms' },
        ].map((step) => (
          <li
            key={step.label}
            className="relative flex items-center gap-4 pb-4 last:pb-0 opacity-0 animate-fade-in-up"
            style={{
              animationDelay: step.delay,
              animationFillMode: 'forwards',
              animationTimingFunction: EASE_OUT_STRONG,
            }}
          >
            <div className="relative shrink-0 h-8 w-8 rounded-full bg-[#1a1638] border border-brand-royal/30 flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              <span className="h-2 w-2 rounded-full bg-brand-periwinkle animate-pulse" />
            </div>
            <p className="text-white/75 text-sm">{step.label}…</p>
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * Friendly redirect when the classifier rejects the URL. Uses the
 * classifier's reason string when present so the user understands why
 * we're asking them to switch — and makes the "switch site" affordance
 * the primary action.
 */
interface NotALawFirmCardProps {
  classifierReason: string | null
  websiteUrl: string
  onTryAnother: () => void
  EASE_OUT_STRONG: string
}

function NotALawFirmCard({ classifierReason, websiteUrl, onTryAnother, EASE_OUT_STRONG }: NotALawFirmCardProps) {
  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-6 py-10 sm:px-10 sm:py-12 opacity-0 animate-fade-in-up"
      style={{
        animationFillMode: 'forwards',
        animationTimingFunction: EASE_OUT_STRONG,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 28px 70px -25px rgba(13,10,31,0.7)',
      }}
    >
      <div className="flex justify-center mb-6">
        <div className="w-14 h-14 rounded-full bg-amber-300/15 ring-1 ring-amber-300/40 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-amber-300" strokeWidth={1.8} />
        </div>
      </div>

      <p className="text-center text-white/85 text-base sm:text-lg leading-relaxed max-w-md mx-auto">
        That doesn't look like a <span className="text-white font-medium">law firm</span> website.
      </p>
      <p className="text-center text-white/55 text-sm mt-3 leading-relaxed max-w-md mx-auto">
        The demo is built around real legal intake — it'll only feel like the real thing when we point
        Molly at <em className="text-white/75 not-italic font-medium">your firm's</em> site.
      </p>

      {classifierReason && (
        <div className="mt-6 mx-auto max-w-md rounded-xl bg-white/[0.03] border border-white/[0.08] px-4 py-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-1.5">
            What we saw at {websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </p>
          <p className="text-white/70 text-sm leading-relaxed italic">"{classifierReason}"</p>
        </div>
      )}

      <button
        onClick={onTryAnother}
        className="mt-8 w-full h-12 rounded-lg bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold text-[15px] active:scale-[0.97] transition-[transform,background-color] duration-150"
        style={{
          transitionTimingFunction: EASE_OUT_STRONG,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 10px 24px -8px rgba(77,113,253,0.45)',
        }}
      >
        Use a different website
      </button>
    </div>
  )
}

/**
 * Beat 2 card — the brief. Personalized firm reveal at the top, a 3-step
 * "how to play" list (mirroring the form's 3-step list treatment), and
 * the primary CTA that fires the actual outbound call.
 *
 * Sits between the analyze beat and the actual phone ring, so the
 * prospect picks up already coached on what to ask.
 */
interface InstructionsCardProps {
  firmName: string
  practiceAreas: string[]
  exampleQuestions: string[]
  isStarting: boolean
  onStart: () => void
  onDifferentFirm: () => void
  EASE_OUT_STRONG: string
}

function InstructionsCard({
  firmName,
  practiceAreas,
  exampleQuestions,
  isStarting,
  onStart,
  onDifferentFirm,
  EASE_OUT_STRONG,
}: InstructionsCardProps) {
  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl px-6 py-10 sm:px-10 sm:py-12 opacity-0 animate-fade-in-up"
      style={{
        animationFillMode: 'forwards',
        animationTimingFunction: EASE_OUT_STRONG,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 28px 70px -25px rgba(13,10,31,0.7)',
      }}
    >
      {/* Firm reveal — small "We found" eyebrow + the firm name in display
          weight. Feels like the analyzer handing off, not a new screen. */}
      <div className="text-center mb-7">
        <p className="text-[11px] uppercase tracking-[0.22em] text-brand-goldlight font-semibold mb-2">
          We found
        </p>
        <p className="text-white font-heading font-semibold text-2xl sm:text-3xl tracking-tight leading-tight">
          {firmName}
        </p>
        {practiceAreas.length > 0 && (
          <p className="mt-3 text-white/55 text-sm">
            Personalizing for{' '}
            <span className="text-white/80">{practiceAreas.slice(0, 3).join(' · ')}</span>
          </p>
        )}
      </div>

      <div aria-hidden className="my-6 mx-auto h-px max-w-[200px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* 3-step "how to play" list — same vertical/connector visual style
          as the form's 3-step process so the experience feels of-a-piece. */}
      <p className="text-center text-[11px] uppercase tracking-[0.22em] text-white/45 font-semibold mb-5">
        How to play
      </p>
      <ol className="relative inline-block text-left mx-auto w-full max-w-md">
        <div
          aria-hidden
          className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-brand-royal/40 via-white/10 to-transparent"
        />
        {[
          {
            num: 1,
            title: 'Pretend you\'re a prospective client',
            detail: `You\'re calling ${firmName.replace(/\s*(Law Firm|Law Offices|Law Office|Law Group|LLC|LLP|PLLC|PC|P\.C\.|& Associates).*/i, '')} for the first time. Get into character.`,
          },
          {
            num: 2,
            title: 'Ask the kind of question your firm gets at intake',
            detail: '',
            chips: exampleQuestions,
          },
          {
            num: 3,
            title: 'Be a little tough on Molly',
            detail: 'Push back, change the subject, get emotional. She\'s built to handle real intake — see what she catches.',
          },
        ].map((step) => (
          <li
            key={step.num}
            className="relative flex items-start gap-4 pb-5 last:pb-0 opacity-0 animate-fade-in-up"
            style={{
              animationFillMode: 'forwards',
              animationTimingFunction: EASE_OUT_STRONG,
              animationDelay: `${step.num * 90}ms`,
            }}
          >
            <div className="relative shrink-0 h-8 w-8 rounded-full bg-[#1a1638] border border-brand-royal/30 flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              <span className="text-brand-periwinkle text-[12px] font-bold tabular-nums">{step.num}</span>
            </div>
            <div className="pt-0.5 flex-1 min-w-0">
              <p className="text-white text-sm font-medium leading-snug">{step.title}</p>
              {step.detail && (
                <p className="text-white/55 text-[13px] leading-relaxed mt-1">{step.detail}</p>
              )}
              {step.chips && step.chips.length > 0 && (
                <ul className="mt-2.5 space-y-1.5">
                  {step.chips.slice(0, 3).map((q, i) => (
                    <li
                      key={i}
                      className="text-[13px] text-white/65 italic leading-relaxed pl-3 border-l border-white/10"
                    >
                      {q}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>

      {/* Primary CTA fires the outbound call. Mirrors the form's submit
          button styling so the visual language carries through. */}
      <button
        onClick={onStart}
        disabled={isStarting}
        className="mt-8 w-full h-12 rounded-lg bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold text-[15px] disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97] transition-[transform,background-color,filter] duration-150 inline-flex items-center justify-center gap-2"
        style={{
          transitionTimingFunction: EASE_OUT_STRONG,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 10px 24px -8px rgba(77,113,253,0.45)',
        }}
      >
        {isStarting ? 'Starting…' : (
          <>
            <PhoneCall className="w-4 h-4" strokeWidth={2} />
            Call me now
          </>
        )}
      </button>

      <button
        onClick={onDifferentFirm}
        className="mt-3 w-full text-[12px] text-white/45 hover:text-white/75 underline underline-offset-4 decoration-white/15 transition-colors"
      >
        Different firm
      </button>
    </div>
  )
}

/**
 * Brief skeleton-ish "building recap" beat between call ended and
 * recap landing. Card chrome matches the completed state so the
 * transition feels like a reveal rather than a layout shift.
 */
interface RecapCardProps {
  firmName: string | null
  EASE_OUT_STRONG: string
}

function RecapCard({ firmName, EASE_OUT_STRONG }: RecapCardProps) {
  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 text-center opacity-0 animate-fade-in-up"
      style={{
        animationFillMode: 'forwards',
        animationTimingFunction: EASE_OUT_STRONG,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 28px 70px -25px rgba(13,10,31,0.7)',
      }}
    >
      <div className="flex justify-center mb-5">
        <div className="w-14 h-14 rounded-full bg-brand-royal/15 ring-1 ring-brand-royal/40 flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-brand-periwinkle animate-pulse" strokeWidth={1.6} />
        </div>
      </div>
      <p className="text-white/85 text-base sm:text-lg leading-relaxed">
        Reviewing what Molly captured…
      </p>
      {firmName && (
        <p className="text-white/45 text-sm mt-2">
          Scoring the intake for <span className="text-white/70">{firmName}</span>
        </p>
      )}

      {/* Skeleton blocks where the recap will land — keeps the layout
          stable so the recap fades in rather than jumping the page. */}
      <div className="mt-7 space-y-3 text-left max-w-md mx-auto">
        <div className="h-3 rounded bg-white/[0.06] animate-pulse" />
        <div className="h-3 rounded bg-white/[0.06] animate-pulse w-5/6" style={{ animationDelay: '150ms' }} />
        <div className="h-3 rounded bg-white/[0.06] animate-pulse w-4/6" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  )
}

/**
 * Renders the score badge + headline + facts captured + topics covered
 * + would-have-booked footer. Sits above the existing summary +
 * transcript inside the completed card.
 */
interface IntakeRecapPanelProps {
  recap: IntakeRecap
}

function IntakeRecapPanel({ recap }: IntakeRecapPanelProps) {
  const score = Math.round(recap.intake_completeness_score * 10)
  const scoreTone = score >= 7
    ? { ring: 'ring-emerald-400/45', text: 'text-emerald-300', bg: 'bg-emerald-400/12', label: 'Strong intake' }
    : score >= 4
      ? { ring: 'ring-amber-300/45', text: 'text-amber-200', bg: 'bg-amber-300/10', label: 'Decent intake' }
      : { ring: 'ring-rose-400/40', text: 'text-rose-200', bg: 'bg-rose-400/10', label: 'Light intake' }

  const bookedTone = recap.would_have_booked
    ? { bg: 'bg-emerald-400/10', ring: 'ring-emerald-400/35', text: 'text-emerald-200', copy: 'Would have booked the consult' }
    : { bg: 'bg-white/[0.04]', ring: 'ring-white/10', text: 'text-white/70', copy: 'Would have warranted a follow-up' }

  return (
    <div className="text-left">
      {/* Score + headline row */}
      <div className="flex items-start gap-5">
        <div
          className={`shrink-0 w-20 h-20 rounded-xl ${scoreTone.bg} ring-1 ${scoreTone.ring} flex flex-col items-center justify-center`}
        >
          <p className={`text-3xl font-semibold tabular-nums leading-none ${scoreTone.text}`}>{score}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-1">/ 10</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${scoreTone.text} mb-1.5`}>
            {scoreTone.label}
          </p>
          <p className="text-white text-base sm:text-[17px] font-medium leading-snug">
            {recap.headline}
          </p>
        </div>
      </div>

      {/* Facts captured */}
      {recap.facts_captured.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold mb-3">
            What Molly captured
          </p>
          <ul className="space-y-2">
            {recap.facts_captured.map((fact, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-white/80 leading-relaxed">
                <svg
                  className="w-3.5 h-3.5 mt-1 shrink-0 text-emerald-400/85"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.4}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>{fact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Topics covered chips */}
      {recap.topics_covered.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold mb-3">
            Topics covered
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recap.topics_covered.map((topic) => (
              <span
                key={topic}
                className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-[11.5px] text-white/70 tracking-wide"
              >
                {topic.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Follow-up actions */}
      {recap.follow_up_actions.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold mb-3">
            What Molly committed to
          </p>
          <ul className="space-y-2">
            {recap.follow_up_actions.map((action, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-white/75 leading-relaxed">
                <svg
                  className="w-3.5 h-3.5 mt-1 shrink-0 text-brand-periwinkle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Outcome pill */}
      <div
        className={`mt-7 inline-flex items-center gap-2 px-3.5 py-2 rounded-full ${bookedTone.bg} ring-1 ${bookedTone.ring}`}
      >
        <svg className={`w-3.5 h-3.5 ${bookedTone.text}`} fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d={
              recap.would_have_booked
                ? 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                : 'M10 18a8 8 0 100-16 8 8 0 000 16zm.93-11.412l-.094.083-3 3-.083.094a1 1 0 001.32 1.497l.094-.083L9 11.586V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.497-1.32l-.083-.094-3-3a1 1 0 00-1.32-.083z'
            }
            clipRule="evenodd"
          />
        </svg>
        <span className={`text-[12.5px] font-medium tracking-tight ${bookedTone.text}`}>
          {bookedTone.copy}
        </span>
      </div>
    </div>
  )
}

interface DemoState {
  status: DemoStatus
  demoId: string | null
  firmName: string | null
  practiceAreas: string[] | null
  caseType: string | null
  classifierReason: string | null
  exampleQuestions: string[] | null
  transcript: string | null
  summary: string | null
  intakeRecap: IntakeRecap | null
  error: string | null
}

export default function DemoPage() {
  const brand = useBrand()
  const [name, setName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [consent, setConsent] = useState(false)
  const [state, setState] = useState<DemoState>({
    status: 'form',
    demoId: null,
    firmName: null,
    practiceAreas: null,
    caseType: null,
    classifierReason: null,
    exampleQuestions: null,
    transcript: null,
    summary: null,
    intakeRecap: null,
    error: null,
  })
  const [formError, setFormError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isStartingCall, setIsStartingCall] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)

  // Real-time updates: SSE primary, with a polling backstop. SSE alone
  // wasn't enough — when the user picks up the phone the browser tab
  // backgrounds (especially on iOS Safari) and Telnyx's post-call AI
  // webhook can take 5-30s after the actual hang-up to land. If the SSE
  // connection drops during that window, the page used to sit on
  // "ringing" forever even though the demo lead was completed in the DB.
  // The poll fires every 5s starting 60s into the call and shuts down
  // once the status resolves.
  useEffect(() => {
    if (
      !state.demoId ||
      state.status === 'form' ||
      state.status === 'completed' ||
      state.status === 'failed' ||
      state.status === 'rejected_not_law_firm'
    ) {
      return
    }

    const demoId = state.demoId

    const applyData = (data: any) => {
      setState(prev => ({
        ...prev,
        status: (data.status as DemoStatus) || prev.status,
        firmName: data.firmName || prev.firmName,
        practiceAreas: data.practiceAreas || prev.practiceAreas,
        caseType: data.caseType || prev.caseType,
        classifierReason: data.classifierReason || prev.classifierReason,
        exampleQuestions: data.exampleQuestions || prev.exampleQuestions,
        transcript: data.transcript || prev.transcript,
        summary: data.summary || prev.summary,
        intakeRecap: data.intakeRecap || prev.intakeRecap,
        error: data.error || null,
      }))
    }

    // Connect to SSE stream
    const eventSource = new EventSource(`${API_BASE_URL}/api/demo/try/${demoId}/stream`)
    eventSource.onmessage = (event) => {
      try { applyData(JSON.parse(event.data)) } catch { /* ignore parse errors */ }
    }
    eventSource.onerror = () => {
      eventSource.close()
      // Single immediate poll so a brief drop still picks up the final state.
      setTimeout(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/demo/try/${demoId}/status`)
          const data = await res.json()
          if (data.success) applyData(data)
        } catch { /* poll loop below covers retries */ }
      }, 2000)
    }

    // Backstop polling — kicks in 60s after this state begins. Stops
    // itself the moment the call completes or fails. Independent of
    // SSE so a permanently-dropped connection still resolves the page.
    let pollInterval: ReturnType<typeof setInterval> | null = null
    const pollTimeout = setTimeout(() => {
      pollInterval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/demo/try/${demoId}/status`)
          const data = await res.json()
          if (data.success) {
            applyData(data)
            if (
              data.status === 'completed' ||
              data.status === 'failed' ||
              data.status === 'rejected_not_law_firm'
            ) {
              if (pollInterval) { clearInterval(pollInterval); pollInterval = null }
            }
          }
        } catch { /* keep polling */ }
      }, 5000)
    }, 60_000)

    return () => {
      eventSource.close()
      clearTimeout(pollTimeout)
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [state.demoId, state.status])

  const formatPhone = (value: string) => {
    // Strip everything except digits and leading +
    const cleaned = value.replace(/[^\d+]/g, '')
    setPhoneNumber(cleaned)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!name.trim() || !websiteUrl.trim() || !phoneNumber.trim()) {
      setFormError('All fields are required')
      return
    }
    if (!consent) {
      setFormError('Please consent to receive the demo call')
      return
    }

    // Basic phone validation
    const digits = phoneNumber.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 15) {
      setFormError('Please enter a valid phone number')
      return
    }

    // Normalize: prepend +1 for 10-digit US numbers
    let normalizedPhone = phoneNumber
    if (!normalizedPhone.startsWith('+')) {
      normalizedPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`
    }

    // Normalize URL
    let url = websiteUrl.trim()
    if (!url.startsWith('http')) {
      url = 'https://' + url
    }

    setIsSubmitting(true)

    try {
      const res = await fetch(`${API_BASE_URL}/api/demo/try`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          websiteUrl: url,
          phoneNumber: normalizedPhone,
          consent: true,
          utmSource: new URLSearchParams(window.location.search).get('utm_source') || undefined,
          utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
          utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setFormError(data.message || 'Something went wrong. Please try again.')
        setIsSubmitting(false)
        return
      }

      // Move to the analyze beat — the SSE stream will push us forward
      // to ready_to_call or rejected_not_law_firm once the classifier
      // returns.
      setState({
        status: 'analyzing',
        demoId: data.id,
        firmName: null,
        practiceAreas: null,
        caseType: null,
        classifierReason: null,
        exampleQuestions: null,
        transcript: null,
        summary: null,
        intakeRecap: null,
        error: null,
      })
    } catch {
      setFormError('Network error. Please check your connection and try again.')
      setIsSubmitting(false)
    }
  }

  const handleStartCall = async () => {
    if (!state.demoId || isStartingCall) return
    setIsStartingCall(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/demo/try/${state.demoId}/start-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) {
        // 409 from the backend means the lead was rejected mid-flight —
        // fall back to that screen rather than the generic failure UI.
        if (res.status === 409) {
          setState(prev => ({
            ...prev,
            status: 'rejected_not_law_firm',
            classifierReason: data?.message || prev.classifierReason || null,
          }))
          setIsStartingCall(false)
          return
        }
        setState(prev => ({
          ...prev,
          status: 'failed',
          error: data?.message || 'We couldn\'t place the demo call. Please try again.',
        }))
        setIsStartingCall(false)
        return
      }
      setState(prev => ({ ...prev, status: 'calling' }))
    } catch {
      setState(prev => ({ ...prev, status: 'failed', error: 'Network error. Please try again.' }))
    } finally {
      setIsStartingCall(false)
    }
  }

  const handleRetry = () => {
    setState({
      status: 'form',
      demoId: null,
      firmName: null,
      practiceAreas: null,
      caseType: null,
      classifierReason: null,
      exampleQuestions: null,
      transcript: null,
      summary: null,
      intakeRecap: null,
      error: null,
    })
    setIsSubmitting(false)
    setIsStartingCall(false)
    setFormError('')
    setShowTranscript(false)
    // Keep websiteUrl so the user can edit rather than re-typing — feels
    // less punishing on a rejection. Name + phone stay too.
  }

  // Emil's preferred curve — "strong" ease-out used across the codebase
  // (see AuthShell). Specified once here so all form-page transitions
  // share the same physics rather than each falling to default cubic-bezier.
  const EASE_OUT_STRONG = 'cubic-bezier(0.23, 1, 0.32, 1)'

  const isFormState = state.status === 'form'
  const isCallingFlight =
    state.status === 'calling' || state.status === 'in_progress'

  return (
    <div className="relative min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-[#0d0a1f] to-[#12103a] px-4 py-10 lg:py-16 overflow-hidden">
      {/* Atmospheric grid + radial blurs — matches legallady.ai hero
          treatment so the demo feels like a continuation of the
          marketing page, not a separate app. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-grid-faint mask-fade-y opacity-40" />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-40 h-[36rem] w-[36rem] rounded-full opacity-50 blur-[140px]"
        style={{ background: 'radial-gradient(circle, rgba(77,113,253,0.32), transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full opacity-40 blur-[120px]"
        style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.28), transparent 70%)' }}
      />

      {/* Back to marketing site — slim affordance top-left so visitors
          don't dead-end on the demo page. */}
      <a
        href="https://www.legallady.ai"
        className="group absolute top-5 left-5 z-20 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-white/55 hover:text-white transition-colors duration-200"
      >
        <svg className="w-3 h-3 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
        </svg>
        legallady.ai
      </a>

      {/* Container width is state-dependent. The form view earns the
          desktop split-screen treatment (hero copy on the left, form
          card on the right); every other state is a focused single
          confirmation card centered at the previous narrow width. */}
      <div className={`relative w-full ${isFormState ? 'max-w-6xl' : 'max-w-xl'}`}>
        {/* Wordmark — sits at the top regardless of state. On desktop
            in the form view it left-aligns so it doesn't dominate the
            left column's negative space. */}
        <div className={`mb-8 ${isFormState ? 'text-center lg:text-left' : 'text-center'}`}>
          <Wordmark
            size="lg"
            variant="light"
            className={isFormState ? 'justify-center lg:justify-start' : 'justify-center'}
          />
        </div>

        {/* FORM STATE — desktop split screen (lg+), single column on mobile */}
        {isFormState && (
          <div className="lg:grid lg:grid-cols-[1.05fr_1fr] lg:gap-14 xl:gap-20 lg:items-center">
            {/* LEFT: hero copy + 3-step process */}
            <div className="text-center lg:text-left max-w-xl mx-auto lg:mx-0 mb-10 lg:mb-0">
              <div
                className="mb-4 flex items-center gap-3 lg:justify-start justify-center opacity-0 animate-fade-in-up"
                style={{ animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
              >
                <span className="w-6 hairline-gold" />
                <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                  Live AI demo · ~30 seconds
                </span>
              </div>
              <h1
                className="text-[2rem] sm:text-[2.5rem] lg:text-[3rem] xl:text-[3.5rem] font-heading font-semibold tracking-tight text-white mb-4 leading-[1.05] opacity-0 animate-fade-in-up"
                style={{ animationDelay: '60ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
              >
                Hear what your{' '}
                <span className="serif-italic text-brand-royal">callers</span>
                <br />
                would hear.
              </h1>
              <p
                className="text-white/55 text-[15px] sm:text-base leading-relaxed max-w-md mx-auto lg:mx-0 mb-8 opacity-0 animate-fade-in-up"
                style={{ animationDelay: '140ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
              >
                Enter your firm's info and we'll call you. Your AI receptionist answers — same voice, same intake, same booking flow your real prospects would experience.
              </p>

              {/* 3-step process: vertical list with a subtle connector
                  rail. Always left-aligned so the bullets line up
                  cleanly across viewports. On mobile the parent box
                  centers the whole hero, but the list stays readable
                  by enforcing its own text-left here. */}
              <ol className="relative inline-block text-left stagger-children">
                <div
                  aria-hidden
                  className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-brand-royal/40 via-white/10 to-transparent"
                />
                {[
                  { icon: Search, title: 'We scan your site', detail: 'Practice areas, hours, tone — pulled in seconds.' },
                  { icon: PhoneCall, title: 'AI calls you back', detail: 'Pick up. The voice answering is your AI receptionist.' },
                  { icon: MessageSquare, title: 'Be your own caller', detail: 'Book a meeting. Ask a question. Try anything you would expect a real prospect to throw at it.' },
                ].map((step, i) => (
                  <li
                    key={step.title}
                    className="relative flex items-start gap-4 pb-5 last:pb-0 opacity-0 animate-fade-in-up"
                    style={{ animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
                  >
                    <div className="relative shrink-0 h-8 w-8 rounded-full bg-[#1a1638] border border-brand-royal/30 flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                      <step.icon className="h-3.5 w-3.5 text-brand-periwinkle" strokeWidth={1.75} />
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-brand-royal text-white text-[9px] font-bold flex items-center justify-center tabular-nums">
                        {i + 1}
                      </span>
                    </div>
                    <div className="pt-0.5">
                      <p className="text-white text-sm font-medium leading-snug">{step.title}</p>
                      <p className="text-white/45 text-[13px] leading-relaxed mt-0.5">{step.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* RIGHT: form card. On desktop it sits in the right column
                and is vertically centered; on mobile it falls under the
                hero copy at full width. */}
            <div className="max-w-xl mx-auto lg:mx-0 lg:max-w-none w-full">
              <div
                className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 opacity-0 animate-fade-in-up"
                style={{
                  animationDelay: '320ms',
                  animationFillMode: 'forwards',
                  animationTimingFunction: EASE_OUT_STRONG,
                  // Inner refraction edge — taste skill: real glass needs an inner
                  // bright top-edge highlight, not just blur. Plus a tinted
                  // (not glowing) drop shadow that matches the page background.
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 60px -20px rgba(13,10,31,0.7)',
                }}
              >
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-white/80">Your Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Maya Reyes"
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-brand-royal/50 focus:border-brand-royal/40 transition-[border-color,box-shadow,background-color] duration-150"
                      style={{ transitionTimingFunction: EASE_OUT_STRONG }}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-white/80">Your Firm's Website</label>
                    <input
                      type="text"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      placeholder="reyesinjurylaw.com"
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-brand-royal/50 focus:border-brand-royal/40 transition-[border-color,box-shadow,background-color] duration-150"
                      style={{ transitionTimingFunction: EASE_OUT_STRONG }}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-white/80">Your Phone Number</label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={e => formatPhone(e.target.value)}
                      placeholder="+1 (415) 802 6118"
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-brand-royal/50 focus:border-brand-royal/40 transition-[border-color,box-shadow,background-color] duration-150 tabular-nums"
                      style={{ transitionTimingFunction: EASE_OUT_STRONG }}
                      required
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => setConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/10 text-brand-royal focus:ring-brand-royal/50 focus:ring-offset-0 shrink-0"
                    />
                    <span className="text-xs text-white/50 leading-relaxed">
                      By clicking "Analyze my firm", I consent to receive a demo phone call from {brand.appName} at the number above. Standard phone charges may apply.
                    </span>
                  </label>

                  {formError && (
                    <div className="rounded-lg bg-red-500/10 border border-red-400/20 px-3.5 py-2.5">
                      <p className="text-red-300 text-sm">{formError}</p>
                    </div>
                  )}

                  {/* Tactile-feedback button — Emil's required pattern. The
                      scale on :active is the "the system heard you" cue.
                      Specific properties only (transform + colors), not `all`.
                      No outer glow — taste skill bans neon glows; the inner
                      highlight + tinted drop does the lift instead. */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 rounded-lg bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold text-[15px] disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97] transition-[transform,background-color,filter] duration-150"
                    style={{
                      transitionTimingFunction: EASE_OUT_STRONG,
                      boxShadow:
                        'inset 0 1px 0 rgba(255,255,255,0.18), 0 10px 24px -8px rgba(77,113,253,0.45)',
                    }}
                  >
                    {isSubmitting ? 'Starting…' : 'Analyze my firm'}
                  </button>

                  <p className="text-center text-[11px] text-white/35 -mb-1">
                    No signup. We read your site, then call when you're ready.
                  </p>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* HEADERS for non-form states — narrow centered layout, branded.
            One header per state group so the eyebrow / headline reads
            distinctly per beat (analyze → brief → ring → recap). */}
        {!isFormState && (
          <div className="text-center mb-8">
            {state.status === 'analyzing' && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Step 1 · Analyze
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  Reading your{' '}
                  <span className="serif-italic text-brand-royal">site</span>.
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  Pulling practice areas and firm details — usually a few seconds.
                </p>
              </>
            )}
            {state.status === 'ready_to_call' && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Step 2 · Brief
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  You're up{' '}
                  <span className="serif-italic text-brand-royal">next</span>.
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  A quick brief, then we'll ring you to start the demo.
                </p>
              </>
            )}
            {state.status === 'rejected_not_law_firm' && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Heads up
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  Not a{' '}
                  <span className="serif-italic text-brand-royal">law firm</span>.
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  Pop in your firm's actual website and we'll personalize the demo.
                </p>
              </>
            )}
            {isCallingFlight && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Incoming · live
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  Your phone is about to{' '}
                  <span className="serif-italic text-brand-royal">ring</span>.
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  Calls usually arrive within 30 seconds — give it up to 45.
                </p>
              </>
            )}
            {state.status === 'recapping' && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Recap
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  Pulling it{' '}
                  <span className="serif-italic text-brand-royal">together</span>…
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  Reviewing what Molly captured during your call.
                </p>
              </>
            )}
            {state.status === 'completed' && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Recap
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  How was{' '}
                  <span className="serif-italic text-brand-royal">that</span>?
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  That&rsquo;s what your clients could experience — 24/7.
                </p>
              </>
            )}
            {state.status === 'failed' && (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="w-6 hairline-gold" />
                  <span className="text-brand-goldlight text-[11px] font-semibold uppercase tracking-[0.22em]">
                    Hiccup
                  </span>
                  <span className="w-6 hairline-gold" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-white mb-3 leading-[1.05]">
                  Something went{' '}
                  <span className="serif-italic text-brand-royal">sideways</span>.
                </h1>
                <p className="text-white/60 text-base sm:text-lg">
                  We hit a snag. Let&rsquo;s try again.
                </p>
              </>
            )}
          </div>
        )}

        {/* Beat 1 — Analyze */}
        {state.status === 'analyzing' && (
          <AnalyzingCard websiteUrl={websiteUrl} EASE_OUT_STRONG={EASE_OUT_STRONG} />
        )}

        {/* Beat 1b — Reject */}
        {state.status === 'rejected_not_law_firm' && (
          <NotALawFirmCard
            classifierReason={state.classifierReason}
            websiteUrl={websiteUrl}
            onTryAnother={handleRetry}
            EASE_OUT_STRONG={EASE_OUT_STRONG}
          />
        )}

        {/* Beat 2 — Brief + Start */}
        {state.status === 'ready_to_call' && (
          <InstructionsCard
            firmName={state.firmName || 'Your firm'}
            practiceAreas={state.practiceAreas || []}
            exampleQuestions={state.exampleQuestions || [
              '"I\'m thinking about hiring a lawyer — can you tell me how this firm works?"',
              '"I\'m not sure if I have a case yet, can I just describe what happened?"',
              '"How quickly can I talk to an actual attorney?"',
            ]}
            isStarting={isStartingCall}
            onStart={handleStartCall}
            onDifferentFirm={handleRetry}
            EASE_OUT_STRONG={EASE_OUT_STRONG}
          />
        )}

        {/* Calling State — Emil + taste-skill polish:
            - Concentric pulse rings (project's existing pulseRingExpand
              keyframe, not generic animate-ping); three rings staggered
              by 0.6s each give a true "incoming-call breathing" feel.
            - Inner core breathes via pulseCoreBreath, no `animate-bounce`
              cheese. Custom ease-in-out via the project CSS variable.
            - Caller ID treatment uses tabular-nums + brand mono shape;
              divider rule under the headline replaces the boxed "card-
              inside-a-card" overuse the taste skill bans.
            - Real glass refraction (inset top highlight + tinted drop),
              not just backdrop-blur.
            - "Already hung up?" affordance after 60s — manual escape
              hatch in case the post-call webhook is late or dropped. */}
        {isCallingFlight && (
          <CallingStateCard
            firmName={state.firmName}
            caseType={state.caseType}
            demoId={state.demoId}
            onForceRefresh={async () => {
              if (!state.demoId) return
              try {
                const res = await fetch(`${API_BASE_URL}/api/demo/try/${state.demoId}/status`)
                const data = await res.json()
                if (data.success) {
                  setState(prev => ({
                    ...prev,
                    status: (data.status as DemoStatus) || prev.status,
                    transcript: data.transcript || prev.transcript,
                    summary: data.summary || prev.summary,
                    intakeRecap: data.intakeRecap || prev.intakeRecap,
                  }))
                }
              } catch { /* swallow — user can try again */ }
            }}
            EASE_OUT_STRONG={EASE_OUT_STRONG}
          />
        )}

        {/* Beat 3a — Recap building */}
        {state.status === 'recapping' && (
          <RecapCard firmName={state.firmName} EASE_OUT_STRONG={EASE_OUT_STRONG} />
        )}

        {/* Beat 3b — Completed: intake recap (if available) above the
            existing summary + transcript + CTAs. */}
        {state.status === 'completed' && (
          <div
            className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 opacity-0 animate-fade-in-up"
            style={{
              animationFillMode: 'forwards',
              animationTimingFunction: EASE_OUT_STRONG,
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.06), 0 28px 70px -25px rgba(13,10,31,0.7)',
            }}
          >
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-emerald-400/15 ring-1 ring-emerald-400/40 flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.4}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              {state.firmName && (
                <p className="text-white/55 text-sm">
                  Demo for <span className="text-white font-medium">{state.firmName}</span>
                </p>
              )}
            </div>

            {/* Intake recap — the new headline panel */}
            {state.intakeRecap && (
              <div className="mb-6 pb-6 border-b border-white/[0.06]">
                <IntakeRecapPanel recap={state.intakeRecap} />
              </div>
            )}

            {/* Call Summary */}
            {state.summary && (
              <div className="p-4 rounded-xl bg-white/[0.04] border border-white/10 text-left">
                <p className="text-white/50 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2">
                  Call summary
                </p>
                <p className="text-white/85 text-sm leading-relaxed">{state.summary}</p>
              </div>
            )}

            {/* Transcript */}
            {state.transcript && (
              <div className="mt-4 text-left">
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  className="flex items-center gap-2 text-white/55 hover:text-white text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors"
                >
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ease-out ${showTranscript ? 'rotate-90' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  {showTranscript ? 'Hide' : 'View'} full transcript
                </button>
                {showTranscript && (
                  <div className="mt-3 p-4 rounded-xl bg-white/[0.04] border border-white/10 max-h-64 overflow-y-auto">
                    {state.transcript.split('\n').map((line, i) => {
                      const isAI = line.startsWith('AI:')
                      return (
                        <p key={i} className={`text-sm leading-relaxed mb-2 ${isAI ? 'text-brand-periwinkle' : 'text-white/65'}`}>
                          <span className={`font-medium ${isAI ? 'text-brand-royal' : 'text-white/85'}`}>
                            {line.split(':')[0]}:
                          </span>
                          {line.substring(line.indexOf(':') + 1)}
                        </p>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Conversion bridge — primary back to marketing, secondary repeat */}
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a
                href="https://www.legallady.ai/pricing"
                className="group flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold text-[15px] active:scale-[0.97] transition-[transform,background-color] duration-200"
                style={{
                  transitionTimingFunction: EASE_OUT_STRONG,
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.18), 0 14px 32px -10px rgba(77,113,253,0.5)',
                }}
              >
                See pricing
                <svg className="w-3.5 h-3.5 transition-transform duration-200 ease-out group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
              <a
                href="https://www.legallady.ai/book-a-demo"
                className="flex-1 inline-flex items-center justify-center py-3.5 rounded-full bg-white/[0.05] ring-1 ring-white/15 text-white font-semibold text-[15px] hover:bg-white/10 hover:ring-white/25 active:scale-[0.97] transition-[transform,background-color,box-shadow] duration-200"
                style={{ transitionTimingFunction: EASE_OUT_STRONG }}
              >
                Talk to a human
              </a>
            </div>
            <button
              onClick={handleRetry}
              className="mt-4 mx-auto block text-[12px] text-white/45 hover:text-white/75 underline underline-offset-4 decoration-white/15 transition-colors"
            >
              Try another demo
            </button>
          </div>
        )}

        {/* Error State */}
        {state.status === 'failed' && (
          <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <p className="text-white/60 mb-6">
              {state.error || 'Something went wrong. Please try again.'}
            </p>
            <button
              onClick={handleRetry}
              className="w-full py-3.5 rounded-lg bg-brand-royal hover:bg-brand-royal/90 text-white font-semibold text-lg transition-all shadow-lg shadow-brand-royal/25"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-6">
          Powered by {brand.appName} &middot; <a href="/terms" className="hover:text-white/50 underline">Terms</a> &middot; <a href="/privacy" className="hover:text-white/50 underline">Privacy</a>
        </p>
      </div>
    </div>
  )
}
